import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Avatar
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ImageUploaderProps {
  label: string;
  currentImageUrl?: string;
  onUpload: (file: File) => Promise<string | null>;
  uploading: boolean;
  progress: number;
  error?: string | null;
  helperText?: string;
  accept?: string;
  maxSizeMB?: number;
  /** Display image as a wide rectangle (e.g. for logos) instead of a square avatar */
  landscape?: boolean;
}

/**
 * Image Uploader Component
 *
 * Features:
 * - Drag and drop support
 * - File picker button
 * - Image preview (current + new)
 * - Upload progress bar
 * - File validation (type, size)
 * - Error messages
 */
const ImageUploader: React.FC<ImageUploaderProps> = ({
  label,
  currentImageUrl,
  onUpload,
  uploading,
  progress,
  error,
  helperText,
  accept = 'image/jpeg,image/jpg,image/png,image/webp',
  maxSizeMB = 5,
  landscape = false
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    const acceptedTypes = accept.split(',').map(t => t.trim());
    if (!acceptedTypes.includes(file.type)) {
      return `Invalid file type. Please upload: ${acceptedTypes.join(', ')}`;
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `File too large. Maximum size is ${maxSizeMB}MB.`;
    }

    return null;
  }, [accept, maxSizeMB]);

  const handleFileChange = useCallback(async (file: File) => {
    setValidationError(null);

    // Validate file
    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    try {
      await onUpload(file);
      // Preview will be replaced by actual URL from server
      setPreviewUrl(null);
    } catch (err) {
      setPreviewUrl(null);
    }
  }, [validateFile, onUpload]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(file);
    }
  }, [handleFileChange]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  }, [handleFileChange]);

  const displayUrl = previewUrl || currentImageUrl;
  const showError = validationError || error;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      {helperText && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {helperText}
        </Typography>
      )}

      <Paper
        variant="outlined"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          p: 2,
          textAlign: 'center',
          border: isDragging ? '2px dashed #1976d2' : '2px dashed #ccc',
          backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
          transition: 'all 0.2s ease',
          cursor: 'pointer'
        }}
      >
        {/* Image Preview */}
        {displayUrl && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            {landscape ? (
              <Box
                component="img"
                src={displayUrl}
                alt={label}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 100,
                  objectFit: 'contain',
                  border: '1px solid #eee',
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: '#fafafa'
                }}
              />
            ) : (
              <Avatar
                src={displayUrl}
                alt={label}
                sx={{
                  width: 120,
                  height: 120,
                  border: '2px solid #eee'
                }}
                variant="rounded"
              />
            )}
          </Box>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Uploading... {progress}%
            </Typography>
          </Box>
        )}

        {/* Success Message */}
        {!uploading && currentImageUrl && !previewUrl && !showError && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
            <Typography variant="body2" color="success.main">
              Upload successful
            </Typography>
          </Box>
        )}

        {/* Upload Button */}
        <input
          accept={accept}
          style={{ display: 'none' }}
          id={`upload-${label.replace(/\s/g, '-')}`}
          type="file"
          onChange={handleInputChange}
          disabled={uploading}
        />
        <label htmlFor={`upload-${label.replace(/\s/g, '-')}`}>
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUploadIcon />}
            disabled={uploading}
          >
            {displayUrl ? 'Change Image' : 'Upload Image'}
          </Button>
        </label>

        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          or drag and drop image here
        </Typography>
      </Paper>

      {/* Error Messages */}
      {showError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {showError}
        </Alert>
      )}
    </Box>
  );
};

export default ImageUploader;
